/**
 * studentSettingsViewModel — ce que « Paramètres » et ses quatre pages lisent dans le store.
 *
 * Patron A : le compte connecté (`currentUser.object`, que `updateStudentAccount` remplace au succès)
 * et les données statiques (les matières, le formulaire du travail personnel, les types de
 * notification, les liens des stores). Les valeurs rendues sont celles du COMPTE : ce que l'élève
 * modifie avant d'enregistrer vit dans l'état de l'écran (`useStudentSettingsState`), qui les
 * recouvre.
 *
 * Seule exception, passée en argument : les notifications déjà basculées (`notificationDraft`,
 * `null` tant que rien n'a bougé) — la liste se rend avec elles. L'argument est lu par sa propre
 * entrée : l'écran le garde dans son état, son identité ne change qu'à une bascule.
 */
import moment from "moment"
import { createSelector } from "reselect"

import {
    canOpenSubscription,
    COUNTRY_OPTIONS,
    toAddressSettings,
    toGeneralSettings,
    toNotificationSettings,
    toProfileCatalogs,
    toProfileSettings,
    toSubscriptionSummary,
    type ApiNotificationSettings,
    type ApiNotificationType,
    type ApiPersonalWorkForm,
    type ApiStudentSettingsUser,
} from "../../../adapters/student/Account/studentSettingsAdapter"
import {
    type UINotificationSetting,
    type UISelectOption,
    type UIStudentAddressSettings,
    type UIStudentGeneralSettings,
    type UIStudentProfileCatalogs,
    type UIStudentProfileSettings,
    type UIStudentSubscriptionSummary,
} from "../../../ui/types/studentAccount"
import { selectAccount } from "../../shared/displayedStudent"

export type StudentSettingsViewModel = {
    /** Le compte n'est pas encore là : chaque page montre son squelette. */
    loading: boolean
    general: UIStudentGeneralSettings | null
    address: UIStudentAddressSettings | null
    profile: UIStudentProfileSettings | null
    catalogs: UIStudentProfileCatalogs
    countries: UISelectOption[]
    profileCompletion: number
    subscription: UIStudentSubscriptionSummary | null
    canOpenSubscription: boolean
    /** `null` tant que le compte n'est pas là. */
    notifications: UINotificationSetting[] | null
    /** Les fiches de l'app dans les stores, servies par le serveur (sinon celles du binaire). */
    storeUrls: { ios: string | null; android: string | null }
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

type SettingsUser = ApiStudentSettingsUser & { profile_completion?: number }

const selectUser = (state: ReduxState): SettingsUser | undefined => selectAccount(state)
const selectSchoolSubjects = (state: ReduxState) => state?.data?.staticData?.school_subjects
const selectPersonalWorkForm = (state: ReduxState): ApiPersonalWorkForm | undefined =>
    state?.data?.staticData?.personal_work_form
const selectNotificationTypes = (state: ReduxState): ApiNotificationType[] | undefined =>
    state?.data?.staticData?.notifications
const selectIosUrl = (state: ReduxState): string | null => state?.data?.staticData?.ios_app_url ?? null
const selectAndroidUrl = (state: ReduxState): string | null => state?.data?.staticData?.android_app_url ?? null
const selectNotificationDraft = (_state: ReduxState, draft: ApiNotificationSettings | null) => draft

export const studentSettingsViewModel = createSelector(
    [
        selectUser,
        selectSchoolSubjects,
        selectPersonalWorkForm,
        selectNotificationTypes,
        selectIosUrl,
        selectAndroidUrl,
        selectNotificationDraft,
    ],
    (user, schoolSubjects, personalWorkForm, notificationTypes, ios, android, draft): StudentSettingsViewModel => ({
        loading: user == null,
        general: user ? toGeneralSettings(user) : null,
        address: user ? toAddressSettings(user) : null,
        profile: user ? toProfileSettings(user) : null,
        catalogs: toProfileCatalogs(schoolSubjects, personalWorkForm),
        countries: COUNTRY_OPTIONS,
        profileCompletion: user?.profile_completion ?? 0,
        subscription: user ? toSubscriptionSummary(user, moment()) : null,
        canOpenSubscription: user ? canOpenSubscription(user) : false,
        notifications: user
            ? toNotificationSettings(notificationTypes, user.roles, draft ?? user.notification_settings)
            : null,
        storeUrls: { ios, android },
    })
)
